import { useState } from 'react'

import {
  useTagAbDecideApi,
  useTagAbStartApi,
  useTagAbStopApi,
  useTagDetailApi,
  useTagListApi,
  useTagRebuildApi,
  useTagSynonymAddApi,
  useTagSynonymDeleteApi,
  useTagUpdateApi
} from '@/common/api/modules/searchTag'
import { TagAbSide, TagAbTest } from '@/common/api/params/searchTag'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

/**
 * 文言A/B。振り分けは**日単位**（互換APIは訪問者IDを受け取らないため、
 * 訪問者単位で出し分けると「その人がどちらを見たか」を後から検証できない）。
 * slug は変えないので、テスト中もタグページのURLは不変。
 */
const AbPanel = ({
  siteId,
  tagId,
  ab,
  onChanged
}: {
  siteId: number
  tagId: number
  ab: TagAbTest | null
  onChanged: (message: string) => void
}) => {
  const [variantName, setVariantName] = useState('')
  const { trigger: start, isMutating: starting } = useTagAbStartApi({
    onSuccess: () => {
      setVariantName('')
      onChanged('文言A/Bを開始しました（slug とURLは変わりません）')
    }
  })
  const { trigger: stop } = useTagAbStopApi({
    onSuccess: () => onChanged('A/Bを中止し、表示名を元に戻しました')
  })
  const { trigger: decide } = useTagAbDecideApi({
    onSuccess: () => onChanged('勝者を確定し、表示名を更新しました')
  })

  const running = ab?.status === 'running'
  const side = (label: string, s: TagAbSide, mark: boolean) => (
    <div
      className={`border-border-input flex-1 rounded-lg border p-3 ${mark ? 'bg-surface-emphasis' : ''}`}
    >
      <Text size={10} color={TextColor.SECONDARY}>
        {label}
      </Text>
      <div>
        <Text size={14} weight='bold'>
          {s.name}
        </Text>
      </div>
      <div className='mt-1'>
        <Text size={12} color={TextColor.SECONDARY}>
          表示 {s.impressions.toLocaleString()} / クリック {s.clicks.toLocaleString()} / CTR{' '}
          {s.ctr === null ? '未計測' : `${s.ctr}%`}
        </Text>
      </div>
    </div>
  )

  return (
    <div className='mt-4'>
      <Heading>文言A/B</Heading>
      {!running ? (
        <>
          {ab && (
            <div className='mt-1'>
              <Text size={12} color={TextColor.SECONDARY}>
                直近のテスト: {ab.control.name} vs {ab.variant.name} —{' '}
                {ab.status === 'decided'
                  ? `${ab.winner === 'variant' ? ab.variant.name : ab.control.name} を採用`
                  : '中止'}
              </Text>
            </div>
          )}
          <Text size={12} color={TextColor.SECONDARY}>
            表示名だけを入れ替えて比べます。slug とタグページのURLは変わりません。
          </Text>
          <div className='mt-2 flex flex-wrap items-center gap-2'>
            <input
              className={textInput}
              placeholder='試す文言'
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
            />
            <Button
              color={ButtonColor.SECONDARY}
              disabled={starting || !variantName.trim()}
              onClick={() =>
                start({ site_id: siteId, tag_id: tagId, variant_name: variantName.trim() })
              }
            >
              A/Bを開始
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className='mt-1'>
            <Text size={12} color={TextColor.SECONDARY}>
              {ab.start_date} 開始・{ab.elapsed_days}日目／本日の配信:{' '}
              {ab.today_variant === 'variant' ? ab.variant.name : ab.control.name}
              （日替わりで入れ替えています）
            </Text>
          </div>
          <div className='mt-2 flex flex-wrap gap-2'>
            {side('現行', ab.control, ab.leading === 'control')}
            {side('テスト', ab.variant, ab.leading === 'variant')}
          </div>
          <div className='mt-2'>
            <Text size={12} color={TextColor.SECONDARY}>
              {!ab.enough_data
                ? `判定にはそれぞれ ${ab.min_impressions_per_side} 回の表示が必要です（まだ判定しません）`
                : ab.leading
                  ? `現時点では「${ab.leading === 'variant' ? ab.variant.name : ab.control.name}」が優勢です`
                  : '差がありません'}
            </Text>
          </div>
          <div className='mt-2 flex flex-wrap items-center gap-2'>
            <Button
              color={ButtonColor.PRIMARY}
              disabled={!ab.enough_data}
              onClick={() =>
                decide({ site_id: siteId, tag_id: tagId, test_id: ab.id, winner: 'control' })
              }
            >
              現行で確定
            </Button>
            <Button
              color={ButtonColor.PRIMARY}
              disabled={!ab.enough_data}
              onClick={() =>
                decide({ site_id: siteId, tag_id: tagId, test_id: ab.id, winner: 'variant' })
              }
            >
              テストで確定
            </Button>
            <Button
              color={ButtonColor.SECONDARY}
              onClick={() => stop({ site_id: siteId, tag_id: tagId, test_id: ab.id })}
            >
              中止して元に戻す
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/** タグ詳細。母集団の状態と同義語をここで扱う */
const TagDetail = ({
  siteId,
  tagId,
  onClose
}: {
  siteId: number
  tagId: number
  onClose: () => void
}) => {
  const { data, mutate } = useTagDetailApi({ site_id: siteId, tag_id: tagId })
  const [name, setName] = useState('')
  const [synonym, setSynonym] = useState('')
  const [note, setNote] = useState('')

  const { trigger: update } = useTagUpdateApi({
    onSuccess: () => {
      setNote('表示名を更新しました（slug は変わりません）')
      mutate()
    }
  })
  const { trigger: rebuild, isMutating: rebuilding } = useTagRebuildApi({
    onSuccess: (r) => {
      setNote(
        `母集団を再形成しました：在庫あり ${r.in_stock} 件` +
          (r.converged ? '（帯内）' : ` — ${r.reason}`)
      )
      mutate()
    }
  })
  const { trigger: addSynonym } = useTagSynonymAddApi({
    onSuccess: () => {
      setSynonym('')
      setNote('同義語を追加しました。母集団に反映するには再形成してください')
      mutate()
    }
  })
  const { trigger: delSynonym } = useTagSynonymDeleteApi({ onSuccess: () => mutate() })

  if (!data) return <Text color={TextColor.SECONDARY}>読み込み中...</Text>

  const t = data.tag
  return (
    <Area>
      <AreaContent>
        <div className='mb-2 flex items-center gap-2'>
          <Heading>{t.tag_name}</Heading>
          {t.pinned && <Text color={TextColor.SECONDARY}>📌 固定</Text>}
          <div className='ml-auto'>
            <Button color={ButtonColor.SECONDARY} onClick={onClose}>
              閉じる
            </Button>
          </div>
        </div>

        <Text size={12} color={TextColor.SECONDARY}>
          slug: <code>{t.tag_slug}</code>（生成後は変更しません。URLの恒久性のため）
        </Text>
        <div className='mt-1'>
          <Text size={12} color={data.converged ? TextColor.SUCCESS : TextColor.CRITICAL}>
            母集団 {t.population} 件 / 在庫あり {t.in_stock_population ?? '—'} 件 （目標帯{' '}
            {data.band_min}〜{data.band_max}）
          </Text>
        </div>
        {data.note && (
          <div className='mt-1'>
            <Text size={12} color={TextColor.SECONDARY}>
              {data.note}
            </Text>
          </div>
        )}
        {note && (
          <div className='bg-surface-emphasis mt-2 rounded-lg p-2'>
            <Text size={12}>{note}</Text>
          </div>
        )}

        <div className='mt-4 flex flex-wrap items-center gap-2'>
          <input
            className={textInput}
            placeholder='表示名を変更'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            color={ButtonColor.SECONDARY}
            onClick={() =>
              name.trim() && update({ site_id: siteId, tag_id: tagId, tag_name: name.trim() })
            }
          >
            表示名を保存
          </Button>
          <Button
            color={ButtonColor.SECONDARY}
            onClick={() => update({ site_id: siteId, tag_id: tagId, pinned: !t.pinned })}
          >
            {t.pinned ? '固定を解除' : '固定する'}
          </Button>
          <Button
            color={ButtonColor.PRIMARY}
            disabled={rebuilding}
            onClick={() => rebuild({ site_id: siteId, tag_id: tagId })}
          >
            {rebuilding ? '再形成中...' : '母集団を再形成'}
          </Button>
        </div>

        <AbPanel
          siteId={siteId}
          tagId={tagId}
          ab={data.ab}
          onChanged={(m) => {
            setNote(m)
            mutate()
          }}
        />

        <div className='mt-4'>
          <Heading>同義語</Heading>
          <div className='mt-1 flex flex-wrap items-center gap-2'>
            {data.synonyms.length === 0 && <Text color={TextColor.SECONDARY}>未登録</Text>}
            {data.synonyms.map((s) => (
              <span key={s} className='bg-surface-secondary rounded-full px-3 py-1 text-[12px]'>
                {s}
                <button
                  type='button'
                  className='ml-2 cursor-pointer'
                  onClick={() => delSynonym({ site_id: siteId, tag_id: tagId, raw_value: s })}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className='mt-2 flex items-center gap-2'>
            <input
              className={textInput}
              placeholder='同義語を追加（表記ゆれ・言い換え）'
              value={synonym}
              onChange={(e) => setSynonym(e.target.value)}
            />
            <Button
              color={ButtonColor.SECONDARY}
              onClick={() =>
                synonym.trim() &&
                addSynonym({ site_id: siteId, tag_id: tagId, raw_value: synonym.trim() })
              }
            >
              追加
            </Button>
          </div>
        </div>

        <div className='mt-4'>
          <Heading>紐づく記事</Heading>
          <Text size={12} color={TextColor.SECONDARY}>
            意味検索でこのタグに近い記事を紐づけています（公開中のもののみ）。母集団の再形成で作り直します。
          </Text>
          <div className='mt-1 flex flex-col gap-1'>
            {data.articles.length === 0 ? (
              <Text color={TextColor.SECONDARY}>
                紐づく記事はありません（記事のベクトルが未投入か、関連が薄いためです）
              </Text>
            ) : (
              data.articles.map((a) => (
                <div key={a.articleId} className='flex flex-wrap items-center gap-2'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    {a.relevance.toFixed(3)}
                  </Text>
                  {a.url ? (
                    <a
                      className='text-emphasis text-[13px] underline'
                      href={a.url}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {a.title}
                    </a>
                  ) : (
                    <Text size={13}>{a.title}</Text>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className='mt-4'>
          <Heading>紐づく商品（上位20件）</Heading>
          <Table>
            <thead>
              <TableRow head>
                <TableHeadItem>
                  <Text size={12}>商品ID</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>商品名</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>関連度</Text>
                </TableHeadItem>
                <TableHeadItem>
                  <Text size={12}>在庫</Text>
                </TableHeadItem>
              </TableRow>
            </thead>
            <tbody>
              {data.products.slice(0, 20).map((p) => (
                <TableRow key={p.product_id}>
                  <TableRowItem>{p.product_id}</TableRowItem>
                  <TableRowItem>{p.name ?? '—'}</TableRowItem>
                  <TableRowItem>{p.relevance.toFixed(3)}</TableRowItem>
                  <TableRowItem>
                    {p.in_stock ? (
                      <Text color={TextColor.SUCCESS}>あり</Text>
                    ) : (
                      <Text color={TextColor.SECONDARY}>なし</Text>
                    )}
                  </TableRowItem>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </div>
      </AreaContent>
    </Area>
  )
}

const SearchTagListPage = () => {
  const siteId = useSiteId()
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const [selected, setSelected] = useState<number | null>(null)

  const { data, isLoading } = useTagListApi({
    site_id: siteId,
    search: keyword || undefined,
    per_page: 100
  })

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='タグ一覧'
        description='承認済みのタグと、その母集団（紐づく商品）を確認します。slug は生成後に変更できません。'
      />
      <Content>
        <Area>
          <AreaContent>
            <div className='mb-3 flex items-center gap-2'>
              <input
                className={textInput}
                placeholder='タグ名で絞り込み'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setKeyword(search.trim())
                }}
              />
              <Button color={ButtonColor.SECONDARY} onClick={() => setKeyword(search.trim())}>
                検索
              </Button>
            </div>

            {isLoading ? (
              <Text color={TextColor.SECONDARY}>読み込み中...</Text>
            ) : !data || data.items.length === 0 ? (
              <Text color={TextColor.SECONDARY}>
                タグがまだありません。承認キューで候補を承認すると作られます。
              </Text>
            ) : (
              <Table>
                <thead>
                  <TableRow head>
                    <TableHeadItem>
                      <Text size={12}>タグ名</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>slug</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>種別</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>母集団</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>状態</Text>
                    </TableHeadItem>
                    <TableHeadItem>
                      <Text size={12}>操作</Text>
                    </TableHeadItem>
                  </TableRow>
                </thead>
                <tbody>
                  {data.items.map((t) => (
                    <TableRow key={t.id}>
                      <TableRowItem>
                        {t.pinned ? '📌 ' : ''}
                        {t.tag_name}
                      </TableRowItem>
                      <TableRowItem>
                        <code>{t.tag_slug}</code>
                      </TableRowItem>
                      <TableRowItem>{t.tag_type}</TableRowItem>
                      <TableRowItem>{t.population}</TableRowItem>
                      <TableRowItem>{t.status}</TableRowItem>
                      <TableRowItem>
                        <Button color={ButtonColor.SECONDARY} onClick={() => setSelected(t.id)}>
                          詳細
                        </Button>
                      </TableRowItem>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </AreaContent>
        </Area>

        {selected != null && (
          <TagDetail siteId={siteId} tagId={selected} onClose={() => setSelected(null)} />
        )}
      </Content>
    </Main>
  )
}

export default SearchTagListPage
